import { useEffect, useState } from 'react';
import { compareRolls } from '@estandarte/engine';
import { useGame } from '../store/game';
import { bossName, territoryName } from '../theme';

const PIPS: Record<number, [number, number][]> = {
  1: [[50, 50]],
  2: [[30, 30], [70, 70]],
  3: [[28, 28], [50, 50], [72, 72]],
  4: [[30, 30], [70, 30], [30, 70], [70, 70]],
  5: [[28, 28], [72, 28], [50, 50], [28, 72], [72, 72]],
  6: [[30, 26], [70, 26], [30, 50], [70, 50], [30, 74], [70, 74]],
};

function Face({ n }: { n: number }) {
  return (
    <svg viewBox="0 0 100 100" aria-hidden>
      {PIPS[n]?.map(([x, y], i) => (
        <circle key={i} cx={x} cy={y} r={9.5} />
      ))}
    </svg>
  );
}

const reduced = () => typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

/** Dado que gira mostrando faces ao acaso e para no valor (o atacante para antes, como no som). */
function Die({ value, kind, lost, idle, delay }: { value: number; kind: 'att' | 'def'; lost?: boolean; idle?: boolean; delay: number }) {
  const [face, setFace] = useState(() => (reduced() ? value : 1 + Math.floor(Math.random() * 6)));
  const [rolling, setRolling] = useState(() => !reduced());
  useEffect(() => {
    if (reduced()) return;
    const iv = setInterval(() => setFace(1 + Math.floor(Math.random() * 6)), 55);
    const stop = setTimeout(() => {
      clearInterval(iv);
      setFace(value);
      setRolling(false);
    }, 380 + delay);
    return () => {
      clearInterval(iv);
      clearTimeout(stop);
    };
  }, [value, delay]);
  return (
    <span
      className={`die ${kind}${rolling ? ' rolling' : ' settled'}${!rolling && lost ? ' lost' : ''}${idle ? ' idle' : ''}`}
      style={{ ['--d' as string]: `${delay}ms` }}
      aria-label={`dado ${value}`}
    >
      <Face n={face} />
    </span>
  );
}

/** Dados do último combate, lado a lado, ordenados, com cada comparação marcada. */
export function DicePanel() {
  const { lastCombat: e, combatSeq, view } = useGame();
  if (!e || !view) return null;
  const att = e.attackRolls;
  const def = e.defenseRolls;
  const pairs = Math.min(att.length, def.length);
  const winsAll = e.type === 'combat' && e.notes.includes('blessing');
  const marks = att.slice(0, pairs).map((a, i) => (winsAll ? false : a > def[i]));
  const out =
    e.type === 'combatPreview' ? compareRolls(att, def) : e.type === 'combat' ? { attackerLoss: e.attackerLoss, defenderLoss: e.defenderLoss } : { attackerLoss: e.attackerLoss, defenderLoss: e.bossLoss };
  const attacker = view.players[e.type === 'bossCombat' ? e.player : e.attacker];
  const defenderLabel =
    e.type === 'bossCombat' ? bossName(e.boss) : e.to ? `${territoryName(e.to)}` : '';
  return (
    <section>
      <h3>
        Dados {e.type === 'combatPreview' && <span className="hint">(antes de aplicar)</span>}
      </h3>
      <div className="dice-row" key={combatSeq}>
        <div className="dice-col">
          <span className="label">{attacker?.name}</span>
          {att.map((v, i) => (
            <Die key={i} value={v} kind="att" lost={i < pairs && !marks[i]} idle={i >= pairs} delay={i * 60} />
          ))}
        </div>
        <div className="vs-col reveal" aria-hidden>
          {marks.map((w, i) => (
            <span key={i} className={w ? 'win' : 'loss'}>
              {w ? '›' : '‹'}
            </span>
          ))}
        </div>
        <div className="dice-col">
          <span className="label">{defenderLabel}</span>
          {def.map((v, i) => (
            <Die key={i} value={v} kind="def" lost={i < pairs && marks[i]} idle={i >= pairs} delay={80 + i * 60} />
          ))}
        </div>
      </div>
      <p className="hint reveal" style={{ margin: '0.4rem 0 0', textAlign: 'center' }}>
        Atacante −{out.attackerLoss} · {e.type === 'bossCombat' ? 'boss' : 'defensor'} −{out.defenderLoss}
        {e.type === 'bossCombat' ? ` · vida ${e.hpLeft}` : ''}
      </p>
    </section>
  );
}
